import React, { memo, useMemo } from 'react';
import { Pressable, View } from 'react-native';

import { PICK_LIST_RECALCULATION_MODE } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import Text from '../../components/text/Text';
import { ActionDock, Card, SecondaryButton, SectionTitle, StopRow } from '../../design/components';
import { STEP_STATUS } from '../../design/components/StatusIcon';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import FreshFoodCard from './components/FreshFoodCard';
import { useDriverDay } from './DriverDayProvider';
import { getStopPositionName, getStopSiteName } from './stopDetails';
import StartOfDayStepScreen from './startOfDay/StartOfDayStepScreen';
import useStartOfDayStep from './startOfDay/useStartOfDayStep';
import useDriverDayActions from './useDriverDayActions';

const RECALCULATION_OPTIONS = [
	{
		mode: PICK_LIST_RECALCULATION_MODE.UNFINISHED,
		title: 'Recalculate unfinished packs',
		description: 'Updates pending and part-picked packs. Keeps what you have already picked.',
	},
	{
		mode: PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL,
		title: 'Recalculate and top up all',
		description: 'Checks every unserviced pack and adds only newly required items.',
	},
];

const getPickTagTone = (row) => {
	if (row.picked) {
		return 'success';
	}

	return row.incompletePick ? 'warning' : 'neutral';
};

// One machine's pick in route order: site and position, machine number and
// pick state; opens its pick list.
const MachinePackRow = memo(({ row, onOpenPickList }) => (
	<StopRow
		place={getStopSiteName(row.docket)}
		title={getStopPositionName(row.docket)}
		machineNumber={row.docket.machine?.assetId}
		labels={[{ label: row.pickStatusLabel, tone: getPickTagTone(row) }]}
		status={row.picked ? STEP_STATUS.DONE : STEP_STATUS.TODO}
		onPress={() => onOpenPickList(row.docket)}
		accessibilityHint="Opens this machine's pick list"
	/>
));

/**
 * Recalculate and top up pick (start of day step): the two recalculation
 * options, the stops with fresh food in route order, pack status per machine
 * in route order (each machine's pick list opens from both, as on the classic
 * My day board), the whole run's pick list summary, and the step's
 * confirmation ("Pick is ready, all tubs loaded", or "Top-up not needed" when
 * Sentry saw newer sales).
 */
const RecalcPickView = () => {
	const styles = useThemedStyles(createStyles);
	const { tenant } = useUserContext();
	const day = useDriverDay();
	const { pickRows, canEditPickLists, isOnline, trainingOffline, hasEligiblePickListItems, freshFoodStops, sortedDockets } = day;
	const { step, isDone, stepLabel, afterThis } = useStartOfDayStep(day, 'packAndPick');
	const {
		openRecalculatePicklistAlert,
		recalculatingPickListsMode,
		markPickTopUpNotNeeded,
		confirmTubsLoaded,
		isConfirmingOnlineStep,
		openPickList,
		openPickListSummary,
	} = useDriverDayActions(day);

	const pickedCount = pickRows.filter(row => row.picked).length;
	const freshFoodRows = useMemo(() => freshFoodStops
		.map(stop => ({ stop, docket: sortedDockets.find(docket => docket.id === stop.serviceDocketId) }))
		.filter(row => row.docket), [freshFoodStops, sortedDockets]);
	const oldestCalculatedAt = useMemo(() => pickRows
		.map(row => row.docket.pickList?.calculatedAt)
		.filter(Boolean)
		.sort()[0], [pickRows]);

	let calculatedLabel = '';
	if (oldestCalculatedAt) {
		const calculated = getLocalTime(tenant?.timezone, oldestCalculatedAt);
		const isToday = calculated.format('YYYY-MM-DD') === day.todayDateKey;
		calculatedLabel = ` at ${calculated.format(isToday ? 'h:mm a' : 'ddd D MMM, h:mm a')}`;
	}

	const renderDock = () => {
		if (isDone) {
			return <ActionDock hint={step?.description} />;
		}
		if (step?.canMarkTopUpNotNeeded) {
			return (
				<ActionDock
					hint={step.description}
					action={{
						label: isOnline ? 'Top-up not needed' : 'Connect to mark top-up not needed',
						onPress: markPickTopUpNotNeeded,
						loading: isConfirmingOnlineStep,
						disabled: !isOnline,
					}}
				/>
			);
		}

		return (
			<ActionDock
				hint={step?.canConfirmTubsLoaded ? afterThis : step?.description}
				action={{
					label: isOnline || !step?.canConfirmTubsLoaded ? 'Pick is ready, all tubs loaded' : 'Connect to confirm tubs are loaded',
					onPress: confirmTubsLoaded,
					loading: isConfirmingOnlineStep,
					disabled: !isOnline || !step?.canConfirmTubsLoaded,
				}}
			/>
		);
	};

	return (
		<StartOfDayStepScreen
			day={day}
			stepLabel={stepLabel}
			heading="Recalculate and top up pick"
			subheading={`Use these if stock has changed since the pick was calculated${calculatedLabel}.`}
			dock={renderDock()}
		>
			{canEditPickLists && RECALCULATION_OPTIONS.map(option => (
				<Card key={option.mode} style={styles.optionCard}>
					<Text style={styles.optionTitle}>{option.title}</Text>
					<Text style={styles.optionDescription}>{option.description}</Text>
					<SecondaryButton
						label={option.title}
						onPress={() => openRecalculatePicklistAlert(option.mode)}
						loading={recalculatingPickListsMode === option.mode}
						disabled={trainingOffline || Boolean(recalculatingPickListsMode)}
					/>
				</Card>
			))}
			{hasEligiblePickListItems && (
				<Pressable
					onPress={openPickListSummary}
					accessibilityRole="link"
					style={({ pressed }) => [styles.link, pressed && styles.pressed]}
				>
					<Text style={styles.linkText}>Pick list summary</Text>
				</Pressable>
			)}
			{freshFoodRows.length > 0 && (
				<View style={styles.section}>
					<SectionTitle title="Fresh food" />
					{freshFoodRows.map(row => (
						<FreshFoodCard
							key={row.stop.serviceDocketId}
							stop={row.stop}
							docket={row.docket}
							onPress={row.docket.pickList?.id ? () => openPickList(row.docket) : undefined}
						/>
					))}
				</View>
			)}
			{pickRows.length > 0 && (
				<View style={styles.section}>
					<SectionTitle title="Pack status" meta={`${pickedCount} of ${pickRows.length} picked`} />
					{pickRows.map(row => (
						<MachinePackRow key={row.docket.id} row={row} onOpenPickList={openPickList} />
					))}
				</View>
			)}
		</StartOfDayStepScreen>
	);
};

const createStyles = ({ ui: { colors, space, touch, type } }) => ({
	optionCard: {
		gap: space.sm,
	},
	optionTitle: {
		...type.sectionTitle,
		color: colors.text,
	},
	optionDescription: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.xs,
	},
	section: {
		gap: space.sm,
	},
	link: {
		minHeight: touch.min,
		justifyContent: 'center',
		alignSelf: 'center',
		paddingHorizontal: space.md,
	},
	linkText: {
		...type.bodySmallStrong,
		color: colors.accent,
	},
	pressed: {
		opacity: 0.7,
	},
});

export default RecalcPickView;
